import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { PlaidService } from 'i2g-ng-payment';
import { I2gDialogService } from 'i2g-ng-ui';
import {
  combineLatest,
  concat,
  EMPTY,
  forkJoin,
  from,
  Observable,
  of,
} from 'rxjs';
import {
  catchError,
  delay,
  distinctUntilChanged,
  filter,
  finalize,
  flatMap,
  map,
  mergeMap,
  share,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { v4 } from 'uuid';
import * as fromApp from '../../store/app.actions';
import { ActionsEnum as AuthActionsEnum } from '../auth-data/auth-data.actions';
import { LocalStorageService } from '../browser-manager-data/local-storage.service';
import { UPDATE_FEATURE_FRAMEWORK_DATA_DELAY } from '../features-data/features-data.data';
import { FeaturesDataDispatcher } from '../features-data/features-data.dispatcher';
import { FeaturesDataStore } from '../features-data/features-data.store';
import { Toggles } from '../features-data/sub-models/toggles.model';
import {
  HomeDataStore,
  setTaskItemState,
  SetTaskItemState,
} from '../home-data';
import {
  TaskItemStateEnum,
  TaskItemTypeEnum,
} from '../home-data/home-data.data';
import { TaskItem } from '../home-data/models/submodels/task-item.model';
import * as fromPayments from '../payments-data/payments-data.actions';
import { TriggerStripeOnboardingCanvas } from '../payments-data/payments-data.actions';
import { PaymentsDataBuilder } from '../payments-data/payments-data.builder';
import { STRIPE_UNDERWRITING_KEY } from '../payments-data/payments-data.data';
import { PaymentsDataService } from '../payments-data/payments-data.service';
// eslint-disable-next-line max-len
import { OnboardingI2gMoneyDialogComponent } from '../payments/components/onboarding-i2g-money-dialog/onboarding-i2g-money-dialog.component';
import { GlobalBannerService } from '../shared/components/global-banner/global-banner.service';
import { MessageToastComponent } from '../shared/components/message-toast/message-toast.component';
// eslint-disable-next-line max-len
import { I2gMoneyOnboardingDialogComponent } from '../shared/components/payments/i2g-money-onboarding-dialog/i2g-money-onboarding-dialog.component';
import { UtilsService } from '../shared/services/utils/utils.service';
import { ShowToast, showToast } from '../toaster/toaster.actions';
import { IToasterConfig, IToasterData } from '../toaster/toaster.data';
import { I2gMoneyContext, I2gMoneyWebview } from './i2g-money.data';
import {
  ActionsEnum,
  emitCurrencyCodeChanged,
  EmitCurrencyCodeChanged,
  emitTaxYearChanged,
  EmitTaxYearChanged,
  fetchI2gMoneyAccountSummarySuccess,
  FetchI2gMoneyAccountSummarySuccess,
  fetchI2gMoneyContextSuccess,
  FetchI2gMoneyContextSuccess,
  fetchI2gMoneyStatementsSuccess,
  FetchI2gMoneyStatementsSuccess,
  FetchI2gMoneyWebviewSuccess,
  fetchI2gMoneyWebviewSuccess,
  fetchPayPalOnboardingUrlSuccess,
  fetchSettings,
  FetchSettings,
  fetchSettingsError,
  FetchSettingsError,
  fetchSettingsSuccess,
  FetchSettingsSuccess,
  FetchStripeOnboardingUrl,
  IFetchPayPalOnboardingUrl,
  IFetchPayPalOnboardingUrlSuccess,
  IUnlinkPayPalAccountError,
  IUnlinkPayPalAccountSuccess,
  OpenI2gMoneyOnboarding,
  saveCurrentSettingsNetwork,
  SaveCurrentSettingsNetwork,
  SaveEditingSettings,
  saveSettingsNetwork,
  SaveSettingsNetwork,
  SaveSettingsNetworkError,
  saveSettingsNetworkError,
  saveSettingsNetworkSuccess,
  SaveSettingsNetworkSuccess,
  SetSettingsDispatchedResponse,
  setSettingsDispatchedResponse,
  SetUnderwritingUrl,
  setUnderwritingUrl,
  SubmitPlaidLinkDetails,
  unlinkPayPalAccountError,
  unlinkPayPalAccountSuccess,
  UpdateBankTransfer,
  UpdateCardPaymentsSettings,
  UpdateCountry,
  updateCountrySuccess,
  UpdateCountrySuccess,
  UpdateCurrency,
  updateCurrencySuccess,
  UpdateCurrencySuccess,
  updateEditingSettings,
  UpdateEditingSettings,
  UpdateI2gMoneyContext,
  updateI2gMoneyContextSuccess,
  UpdateI2gMoneyContextSuccess,
  UpdateInitialPaymentReceiptsSettings,
  UpdateLocale,
  UpdateLocaleSuccess,
  updateLocaleSuccess,
  UpdateMfaSettings,
  UpdatePaypalSettings,
  updateSettings,
  UpdateSettings,
} from './settings-data.actions';
import { SettingsDataBuilder } from './settings-data.builder';
import { SettingsDataDispatcher } from './settings-data.dispatcher';
import { SettingsDataService } from './settings-data.service';
import { SettingsDataStore } from './settings-data.store';
import { Settings } from './settings.model';
import { ManualPaymentReceiptsEnabledEnum } from './sub-models/company-settings/company-settings.model';
import { PaymentsSettings } from './sub-models/company-settings/payments.model';

@Injectable()
export class SettingsDataEffects {
  constructor(
    private _actions: Actions,
    private _settingsDataService: SettingsDataService,
    private _settingsDataStore: SettingsDataStore,
    private _homeDataStore: HomeDataStore,
    private _settingsDataDispatcher: SettingsDataDispatcher,
    private _featuresDataDispatcher: FeaturesDataDispatcher,
    private _featuresDataStore: FeaturesDataStore,
    private _paymentsDataBuilder: PaymentsDataBuilder,
    private _paymentsDataService: PaymentsDataService,
    private _settingsDataBuilder: SettingsDataBuilder,
    private _utilsService: UtilsService,
    private _globalBannerService: GlobalBannerService,
    private _localStorageService: LocalStorageService,
    private _translateService: TranslateService,
    private _i2gDialogService: I2gDialogService,
    private _plaidService: PlaidService
  ) {}

  @Effect()
  onAuthAndRefreshSettings: Observable<FetchSettings> = this._actions.pipe(
    ofType(AuthActionsEnum.AUTHENTICATED),
    take(1),
    map(() => fetchSettings(v4()))
  );

  @Effect()
  getSettings: Observable<
    UpdateSettings | FetchSettingsSuccess | FetchSettingsError
  > = this._actions.pipe(
    ofType(ActionsEnum.FETCH_SETTINGS),
    switchMap(({ payload }: FetchSettings) => {
      const { dispatchId, updateEditingSettings: updatedSettings } = payload;

      return this._settingsDataService.getSettings().pipe(
        flatMap((settings: Settings) => {
          const effects = [
            updateSettings(settings, false, updatedSettings),
            fetchSettingsSuccess(dispatchId),
          ];

          return concat(effects);
        }),
        catchError((error) => of(fetchSettingsError(dispatchId, error)))
      );
    })
  );

  @Effect()
  onUpdateSettings: Observable<UpdateEditingSettings> = this._actions.pipe(
    ofType(ActionsEnum.UPDATE_SETTINGS),
    filter(({ payload }: UpdateSettings) => payload.updateEditingSettings),
    map(({ payload }: UpdateSettings) =>
      updateEditingSettings(payload.settingsProps)
    )
  );

  @Effect()
  onSetEditingSettingsToCurrent: Observable<UpdateEditingSettings> = this._actions.pipe(
    ofType(ActionsEnum.SET_EDITING_SETTINGS_TO_CURRENT),
    flatMap(() => this._settingsDataStore.getSettings()),
    map(updateEditingSettings)
  );

  @Effect()
  updateCountry: Observable<
    UpdateSettings | UpdateCountrySuccess
  > = this._actions.pipe(
    ofType(ActionsEnum.UPDATE_COUNTRY),
    switchMap(({ payload }: UpdateCountry) => {
      return this._saveSettingsIfChanged().pipe(
        flatMap(() => this._settingsDataService.updateCountry(payload.country)),
        flatMap((settings: Settings) =>
          concat([
            updateSettings(settings, true, payload.updateEditingSettings),
            updateCountrySuccess(payload.dispatchId, settings),
          ])
        )
      );
    })
  );

  @Effect()
  updateCurrency: Observable<
    UpdateSettings | UpdateCurrencySuccess
  > = this._actions.pipe(
    ofType(ActionsEnum.UPDATE_CURRENCY),
    switchMap(({ payload }: UpdateCurrency) => {
      return this._saveSettingsIfChanged().pipe(
        flatMap(() =>
          this._settingsDataService.updateCurrency(payload.currency)
        ),
        flatMap((settings: Settings) =>
          concat([
            updateSettings(settings, true, payload.updateEditingSettings),
            updateCurrencySuccess(payload.dispatchId, settings),
          ])
        )
      );
    })
  );

  @Effect()
  updateLocale: Observable<
    UpdateSettings | UpdateLocaleSuccess
  > = this._actions.pipe(
    ofType(ActionsEnum.UPDATE_LOCALE),
    switchMap(({ payload }: UpdateLocale) => {
      return this._saveSettingsIfChanged().pipe(
        flatMap(() =>
          this._settingsDataService.updateDocumentLocale(payload.locale)
        ),
        flatMap((settings: Settings) =>
          concat([
            updateSettings(settings, true, payload.updateEditingSettings),
            updateLocaleSuccess(payload.dispatchId, settings),
          ])
        )
      );
    })
  );

  @Effect({ dispatch: false })
  updateFeaturesData: Observable<any> = this._actions.pipe(
    ofType(
      ActionsEnum.UPDATE_COUNTRY_SUCCESS,
      ActionsEnum.UPDATE_CURRENCY_SUCCESS,
      ActionsEnum.UPDATE_LOCALE_SUCCESS
    ),
    switchMap(
      ({
        payload,
      }: UpdateCountrySuccess | UpdateCurrencySuccess | UpdateLocaleSuccess) =>
        of(void 0) // `of()` and `EMPTY` doesn't actually trigger the flatMap
          .pipe(
            delay(UPDATE_FEATURE_FRAMEWORK_DATA_DELAY),
            tap(() =>
              this._featuresDataDispatcher.fetchFeaturesData(payload.dispatchId)
            )
          )
    )
  );

  @Effect()
  updateBankTransfer: Observable<
    UpdateSettings | SetSettingsDispatchedResponse
  > = this._actions.pipe(
    ofType(ActionsEnum.UPDATE_BANK_TRANSFER),
    withLatestFrom(this._settingsDataStore.settings$),
    switchMap(([{ payload }, settings]: [UpdateBankTransfer, Settings]) => {
      const { dispatchId, enabled } = payload;
      const cardPaymentsPayload = this._paymentsDataBuilder.buildBankTransferPayload(
        enabled
      );

      return this._settingsDataService
        .saveBankTransfer(cardPaymentsPayload)
        .pipe(
          map((payments: PaymentsSettings) =>
            this._settingsDataBuilder.buildSettingsFromPaymentSettings(
              settings,
              payments
            )
          ),
          flatMap((newSettings: Settings) =>
            concat([
              updateSettings(newSettings, true, true),
              setSettingsDispatchedResponse(dispatchId, newSettings),
            ])
          ),
          catchError((error) =>
            of(setSettingsDispatchedResponse(dispatchId, settings, error))
          )
        );
    })
  );

  @Effect()
  updateCardPaymentsSettings: Observable<
    | UpdateSettings
    | SetSettingsDispatchedResponse
    | TriggerStripeOnboardingCanvas
  > = this._actions.pipe(
    ofType(ActionsEnum.UPDATE_CARD_PAYMENTS_SETTINGS),
    withLatestFrom(this._settingsDataStore.settings$),
    switchMap(
      ([{ payload }, settings]: [UpdateCardPaymentsSettings, Settings]) => {
        const { dispatchId, enabled, href } = payload;
        const cardPaymentsPayload = this._paymentsDataBuilder.buildCardPaymentsPayload(
          enabled,
          settings.companySettings.country
        );

        return this._settingsDataService
          .saveCardPaymentsSettings(cardPaymentsPayload)
          .pipe(
            flatMap((payments: PaymentsSettings) => {
              const newSettings = this._settingsDataBuilder.buildSettingsFromPaymentSettings(
                settings,
                payments
              );
              const effects: any[] = [
                updateSettings(newSettings, true, true),
                setSettingsDispatchedResponse(dispatchId, newSettings),
              ];

              if (
                payments.cardPayments &&
                payments.cardPayments.stripe &&
                payments.cardPayments.stripe.isUnderwritingRequired()
              ) {
                effects.push(
                  fromPayments.triggerStripeOnboardingCanvas(false, href)
                );
              }

              return concat(effects);
            }),
            catchError((error) => {
              this._globalBannerService.showSystemErrorBanner(error);

              return concat([
                updateSettings(settings, true, true),
                setSettingsDispatchedResponse(dispatchId, settings, error),
              ]);
            })
          );
      }
    )
  );

  @Effect()
  checkStripeUnderwriting: Observable<ShowToast> = this._actions.pipe(
    ofType(fromApp.ActionsEnum.INIT_APP),
    take(1), // only check it once on loading the app
    filter(
      () =>
        this._localStorageService.getItem(STRIPE_UNDERWRITING_KEY) === 'true'
    ),
    switchMap(() => {
      this._localStorageService.removeItem(STRIPE_UNDERWRITING_KEY);

      return this._settingsDataStore.getSettings().pipe(
        filter((settings: Settings) =>
          settings.companySettings.payments.cardPaymentsEnabled()
        ),
        delay(5000),
        map(() => {
          const label = this._translateService.instant(
            'PAYMENTS.STRIPE.UNDERWRITING_SUCCESS'
          );
          const toastData: IToasterData = { label };
          const toastConfig: IToasterConfig = {
            type: 'underwriting-stripe-succes',
            component: MessageToastComponent,
            data: toastData,
          };

          return showToast(toastConfig);
        })
      );
    })
  );

  @Effect()
  fetchStripeOnboardingUrl: Observable<SetUnderwritingUrl> = this._actions.pipe(
    ofType(ActionsEnum.FETCH_STRIPE_ONBOARDING_URL),
    switchMap(({ payload }: FetchStripeOnboardingUrl) => {
      const { enableCardPayments, returnUrl } = payload;
      const req: Observable<string> = enableCardPayments
        ? this._settingsDataService.getStripeOnboardingUrl(returnUrl)
        : this._paymentsDataService.getStripeOnboardingUrl(returnUrl);

      return req.pipe(
        map((underwritingUrl: string) => setUnderwritingUrl(underwritingUrl)),
        catchError((error) => {
          this._globalBannerService.showSystemErrorBanner(error);

          return EMPTY;
        })
      );
    })
  );

  @Effect()
  updatePaypalSettings: Observable<
    UpdateSettings | SetSettingsDispatchedResponse
  > = this._actions.pipe(
    ofType(ActionsEnum.UPDATE_PAYPAL_SETTINGS),
    withLatestFrom(this._settingsDataStore.settings$),
    switchMap(([{ payload }, settings]: [UpdatePaypalSettings, Settings]) => {
      const { dispatchId, enabled } = payload;
      const paypalPayload = this._paymentsDataBuilder.buildPaypalPayload(
        enabled
      );

      return this._settingsDataService.savePaypalSettings(paypalPayload).pipe(
        flatMap((payments: PaymentsSettings) => {
          const newSettings = this._settingsDataBuilder.buildSettingsFromPaymentSettings(
            settings,
            payments
          );

          return concat([
            updateSettings(newSettings, true, true),
            setSettingsDispatchedResponse(dispatchId, newSettings),
          ]);
        }),
        catchError((error) =>
          of(setSettingsDispatchedResponse(dispatchId, settings, error))
        )
      );
    })
  );

  @Effect()
  updateMfaSettings: Observable<
    UpdateSettings | SaveCurrentSettingsNetwork
  > = this._actions.pipe(
    ofType(ActionsEnum.UPDATE_MFA_SETTINGS),
    withLatestFrom(this._settingsDataStore.settings$),
    switchMap(([{ payload }, settings]: [UpdateMfaSettings, Settings]) => {
      const { enabled } = payload;

      const newSettings = this._settingsDataBuilder.buildSettingsForMfa(
        settings,
        enabled
      );

      return concat([
        updateSettings(newSettings, true, true),
        saveCurrentSettingsNetwork(v4()),
      ]);
    })
  );

  @Effect()
  updateInitialPaymentReceiptsSettings: Observable<
    UpdateSettings | SaveCurrentSettingsNetwork
  > = this._actions.pipe(
    ofType(ActionsEnum.UPDATE_INITIAL_PAYMENT_RECEIPTS_SETTINGS),
    withLatestFrom(this._settingsDataStore.settings$),
    filter(([, settings]: [UpdateInitialPaymentReceiptsSettings, Settings]) => {
      return (
        settings.companySettings.manualPaymentReceiptsEnabled ===
        ManualPaymentReceiptsEnabledEnum.never_toggled
      );
    }),
    switchMap(
      ([{ payload }]: [UpdateInitialPaymentReceiptsSettings, Settings]) => {
        const { enabled } = payload;
        const props = this._settingsDataBuilder.buildPaymentReceiptsSettingsProps(
          enabled
        );

        return concat([
          updateSettings(props),
          saveCurrentSettingsNetwork(v4()),
        ]);
      }
    )
  );

  @Effect()
  saveSettings: Observable<
    | UpdateSettings
    | SaveSettingsNetworkSuccess
    | SaveSettingsNetworkError
    | EmitTaxYearChanged
  > = this._actions.pipe(
    ofType(ActionsEnum.SAVE_SETTINGS_NETWORK),
    switchMap(({ payload }: SaveSettingsNetwork) => {
      const { dispatchId, settings } = payload;

      return this._settingsDataService.saveSettings(settings).pipe(
        withLatestFrom(this._settingsDataStore.getOriginalEditingSettings()),
        flatMap(([newSettings, previousSettings]: Settings[]) => {
          const effects: any[] = [
            updateSettings(newSettings),
            saveSettingsNetworkSuccess(
              dispatchId,
              newSettings,
              previousSettings
            ),
          ];
          const newTaxYearStart = newSettings.companySettings.taxYearStart;
          const previousTaxYearStart =
            previousSettings && previousSettings.companySettings.taxYearStart;

          if (
            previousTaxYearStart &&
            !newTaxYearStart.equals(previousTaxYearStart)
          ) {
            effects.push(emitTaxYearChanged());
          }

          return concat(effects);
        }),
        catchError((error) => of(saveSettingsNetworkError(dispatchId, error)))
      );
    }),
    share()
  );

  /**
   * For FTU task list in homescreen, check if any settings changes can mark
   * task item as completed
   */
  @Effect()
  checkSettingsForTaskList: Observable<SetTaskItemState> = this._actions.pipe(
    ofType(ActionsEnum.SAVE_SETTINGS_NETWORK_SUCCESS),
    filter(
      ({ payload }: SaveSettingsNetworkSuccess) =>
        Boolean(payload.previousSettings) && Boolean(payload.settings)
    ),
    switchMap(({ payload }: SaveSettingsNetworkSuccess) => {
      return this._homeDataStore.hasTaskItemWithIncompleteState().pipe(
        filter((x) => !!x),
        switchMap(() => {
          const output = this._utilsService.checkSettingsForTaskList(
            payload.previousSettings,
            payload.settings
          );
          return from(output.tasks_to_complete);
        }),
        flatMap((taskItemToComplete) => {
          return this._homeDataStore
            .getTaskItemByTypeAndState(
              taskItemToComplete as TaskItemTypeEnum,
              TaskItemStateEnum.incomplete
            )
            .pipe(
              filter((x) => !!x),
              map((taskItem: TaskItem) =>
                setTaskItemState(taskItem.id, TaskItemStateEnum.completed)
              )
            );
        })
      );
    })
  );

  @Effect()
  saveCurrentSettings: Observable<SaveSettingsNetwork> = this._actions.pipe(
    ofType(ActionsEnum.SAVE_CURRENT_SETTINGS_NETWORK),
    switchMap(({ payload }: SaveCurrentSettingsNetwork) =>
      this._settingsDataStore
        .getSettings()
        .pipe(
          map((settings: Settings) =>
            saveSettingsNetwork(payload.dispatchId, settings)
          )
        )
    )
  );

  @Effect()
  saveEditingSettings: Observable<
    UpdateSettings | SaveSettingsNetworkSuccess
  > = this._actions.pipe(
    ofType(ActionsEnum.SAVE_EDITING_SETTINGS),
    switchMap(({ payload }: SaveEditingSettings) => {
      const { dispatchId } = payload;

      return forkJoin(
        this._settingsDataStore.getEditingSettings(),
        this._settingsDataStore.getSettings()
      ).pipe(
        tap(([editingSettings]: Settings[]) =>
          this._settingsDataDispatcher.updateSettings(editingSettings)
        ),
        flatMap(
          ([editingSettings, currentSettings]: Settings[]): Observable<
            UpdateSettings | SaveSettingsNetworkSuccess
          > => {
            if (!editingSettings.content.equals(currentSettings.content)) {
              return this._settingsDataDispatcher
                .saveSettingsNetwork(editingSettings, dispatchId)
                .pipe(
                  map((settings: Settings) => updateSettings(settings)),
                  catchError(() => of(updateSettings(editingSettings)))
                );
            } else {
              return of(
                saveSettingsNetworkSuccess(
                  dispatchId,
                  editingSettings,
                  currentSettings
                )
              ).pipe(delay(500));
            }
          }
        )
      );
    })
  );

  @Effect()
  onCurrencyCodeChanged: Observable<EmitCurrencyCodeChanged> = this._settingsDataStore.settings$.pipe(
    filter((x) => !!x),
    map((settings: Settings) => settings.documentPresetSettings.currencyCode),
    distinctUntilChanged(),
    map((currencyCode: string) => emitCurrencyCodeChanged(currencyCode))
  );

  private _saveSettingsIfChanged = (): Observable<any> => {
    return this._settingsDataStore.getSettings().pipe(
      withLatestFrom(this._settingsDataStore.editingSettings$),
      flatMap(
        ([currentSettings, editingSettings]: Settings[]): Observable<any> => {
          if (currentSettings && !currentSettings.equals(editingSettings)) {
            const saveSettingsDispatchId = v4();

            this._settingsDataDispatcher.saveSettingsNetwork(
              editingSettings || currentSettings,
              saveSettingsDispatchId
            );

            return this._actions.pipe(
              ofType(ActionsEnum.SAVE_SETTINGS_NETWORK_SUCCESS),
              filter(
                ({ payload }: SaveSettingsNetworkSuccess) =>
                  payload.dispatchId === saveSettingsDispatchId
              )
            );
          } else {
            return of(currentSettings);
          }
        }
      )
    );
  };

  /** I2G MONEY ONBOARDING */
  @Effect()
  fetchI2gMoneyWebview(): Observable<FetchI2gMoneyWebviewSuccess | never> {
    return combineLatest([
      this._actions.pipe(ofType(ActionsEnum.FETCH_I2G_MONEY_WEBVIEW)),
      this._featuresDataStore.featureToggles$,
    ]).pipe(
      switchMap(([, toggles]) => {
        if (toggles.isAllAccess(Toggles.KEYS.INVOICE2GO_MONEY)) {
          return this._settingsDataService.fetchI2gMoneyWebview().pipe(
            map((value: I2gMoneyWebview) => fetchI2gMoneyWebviewSuccess(value)),
            catchError(() => EMPTY)
          );
        }

        return EMPTY;
      })
    );
  }

  @Effect()
  fetchI2gMoneyContext(): Observable<FetchI2gMoneyContextSuccess | never> {
    return combineLatest([
      this._actions.pipe(ofType(ActionsEnum.FETCH_I2G_MONEY_CONTEXT)),
      this._featuresDataStore.featureToggles$,
    ]).pipe(
      switchMap(([, toggles]) => {
        if (toggles.isAllAccess(Toggles.KEYS.INVOICE2GO_MONEY)) {
          return this._settingsDataService.fetchI2gMoneyContext().pipe(
            map((value: I2gMoneyContext) => fetchI2gMoneyContextSuccess(value)),
            catchError(() => EMPTY)
          );
        }

        return EMPTY;
      })
    );
  }

  @Effect()
  updateI2gMoneyContext: Observable<UpdateI2gMoneyContextSuccess> = this._actions.pipe(
    ofType(ActionsEnum.UPDATE_I2G_MONEY_CONTEXT),
    switchMap(({ payload }: UpdateI2gMoneyContext) => {
      return this._settingsDataService.updateI2gMoneyContext(payload).pipe(
        map((value: I2gMoneyContext['settings']) =>
          updateI2gMoneyContextSuccess(value)
        ),
        catchError((error) => {
          this._globalBannerService.showSystemErrorBanner(error);

          return EMPTY;
        })
      );
    })
  );

  @Effect({ dispatch: false })
  startI2gMoneyOnboarding = this._actions.pipe(
    ofType(ActionsEnum.OPEN_I2G_MONEY_ONBOARDING),
    map(({ payload }: OpenI2gMoneyOnboarding) =>
      this._i2gDialogService.openDialog(OnboardingI2gMoneyDialogComponent, {
        ...payload,
        isDismissible: true,
      })
    )
  );

  @Effect({ dispatch: false })
  finishI2gMoneyOnboarding = this._actions.pipe(
    ofType(ActionsEnum.CLOSE_I2G_MONEY_ONBOARDING),
    map(() => this._i2gDialogService.closeDialog())
  );

  @Effect({ dispatch: false })
  initializePlaid = this._actions.pipe(
    ofType(ActionsEnum.INITIALIZE_PLAID),
    tap(() => {
      this._plaidService.loadScript();
    })
  );

  @Effect()
  linkExternalBankAccount: Observable<
    FetchI2gMoneyContextSuccess | never
  > = this._actions.pipe(
    ofType(ActionsEnum.LINK_EXTERNAL_BANK_ACCOUNT),
    switchMap(({ payload }: SubmitPlaidLinkDetails) => {
      return this._settingsDataService.linkExternalBankAccount(payload).pipe(
        map((value: I2gMoneyContext) => {
          // Finish 'processing'
          this._settingsDataDispatcher.finishProcessing();
          return fetchI2gMoneyContextSuccess(value);
        }),
        catchError((error) => {
          // Finish 'processing'
          this._settingsDataDispatcher.finishProcessing();

          // Show error
          this._globalBannerService.showSystemErrorBanner(error);

          return EMPTY;
        })
      );
    })
  );

  @Effect()
  unlinkExternalBankAccount: Observable<
    FetchI2gMoneyContextSuccess | never
  > = this._actions.pipe(
    ofType(ActionsEnum.UNLINK_EXTERNAL_BANK_ACCOUNT),
    switchMap(() => {
      return this._settingsDataService.unlinkExternalBankAccount().pipe(
        tap(() => {
          // Finish 'processing'
          this._settingsDataDispatcher.finishProcessing();

          // Close dialog
          this._i2gDialogService.closeDialog();
        }),
        map((value: I2gMoneyContext) => fetchI2gMoneyContextSuccess(value)),
        catchError((error) => {
          // Show error
          this._globalBannerService.showSystemErrorBanner(error);

          return EMPTY;
        })
      );
    })
  );

  /**
   * Paypal
   */
  @Effect()
  fetchPayPalOnboardingUrl: Observable<
    IFetchPayPalOnboardingUrlSuccess | FetchSettings
  > = this._actions.pipe(
    ofType(ActionsEnum.FETCH_PAYPAL_ONBOARDING_URL),
    mergeMap(({ payload }: IFetchPayPalOnboardingUrl) => {
      const { returnUrl } = payload;

      return this._settingsDataService.getPayPalOnboardingUrl(returnUrl).pipe(
        mergeMap((url: string) => {
          const actions = [];

          /**
           * if onboarding_url user is null, user has probably completed onboarding,
           * so we can close the dialog and fetch settings to update the UI
           **/
          if (url === null) {
            this._i2gDialogService.closeDialog();
            actions.push(fetchSettings(v4(), true));
          }

          actions.push(fetchPayPalOnboardingUrlSuccess(url));

          return concat(actions);
        }),
        catchError((error) => {
          this._globalBannerService.showSystemErrorBanner(error);
          this._i2gDialogService.closeDialog();

          return EMPTY;
        })
      );
    })
  );

  @Effect()
  fetchAccountSummary: Observable<
    FetchI2gMoneyAccountSummarySuccess | never
  > = this._actions.pipe(
    ofType(ActionsEnum.FETCH_I2G_MONEY_ACCOUNT_SUMMARY),
    switchMap(() => {
      return this._settingsDataService.fetchAccountSummary().pipe(
        map((value) => fetchI2gMoneyAccountSummarySuccess(value)),
        catchError(() => EMPTY)
      );
    })
  );

  @Effect()
  fetchStatements: Observable<
    FetchI2gMoneyStatementsSuccess | never
  > = this._actions.pipe(
    ofType(ActionsEnum.FETCH_I2G_MONEY_STATEMENTS),
    switchMap(() => {
      return this._settingsDataService.fetchStatements().pipe(
        map((value) => fetchI2gMoneyStatementsSuccess(value)),
        catchError(() => EMPTY)
      );
    })
  );

  unlinkPayPalAccount: Observable<
    IUnlinkPayPalAccountSuccess | FetchSettings | IUnlinkPayPalAccountError
  > = this._actions.pipe(
    ofType(ActionsEnum.UNLINK_PAYPAL_ACCOUNT),
    mergeMap(() => {
      this._i2gDialogService.openLoadingDialog();

      return this._settingsDataService.unlinkPayPalAccount().pipe(
        mergeMap(() =>
          concat([unlinkPayPalAccountSuccess(), fetchSettings(v4(), true)])
        ),
        catchError((error) => {
          this._globalBannerService.showSystemErrorBanner(error);

          return of(unlinkPayPalAccountError(error));
        }),
        finalize(() => this._i2gDialogService.closeDialog())
      );
    })
  );
}
